import { useState } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, Loader2, ChevronLeft, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useSecurity } from "../context/SecurityContext";
import { requestResetEmail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const EmailOption = ({ hint }) => {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await requestResetEmail();
      setSent(true);
      toast.success(`Reset link sent to ${hint}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the email");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="reset-email-option" className="rounded-xl border border-border bg-card p-4 text-left">
      <div className="text-sm font-medium flex items-center gap-1.5">
        <Mail className="h-4 w-4 text-primary" /> Email me a reset link
      </div>
      <p className="text-xs text-muted-foreground mt-1">We'll send a single-use link to {hint}. It expires in 30 minutes.</p>
      <Button data-testid="send-reset-email-btn" onClick={send} disabled={busy || sent} size="sm" className="mt-3 w-full gap-2 bg-primary hover:bg-primary/90 rounded-full">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        {sent ? "Link sent — check your inbox" : "Send reset link"}
      </Button>
    </div>
  );
};

const CodeOption = () => {
  const { recoverPin } = useSecurity();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!code.trim()) return toast.error("Enter the reset code");
    if (!/^\d{4,6}$/.test(pin)) return toast.error("New PIN must be 4–6 digits");
    if (pin !== confirm) return toast.error("PINs don't match");
    setBusy(true);
    try {
      await recoverPin(code.trim(), pin);
      toast.success("Owner PIN reset — you're in");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset the PIN");
      setBusy(false);
    }
    return undefined;
  };

  return (
    <div data-testid="reset-code-option" className="rounded-xl border border-border bg-card p-4 text-left">
      <button data-testid="use-reset-code-btn" onClick={() => setOpen((v) => !v)} className="w-full text-left">
        <div className="text-sm font-medium flex items-center gap-1.5">
          <KeyRound className="h-4 w-4 text-primary" /> Use the vault's reset code
        </div>
        <p className="text-xs text-muted-foreground mt-1">The secret set by whoever deployed this vault.</p>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <Input data-testid="reset-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Reset code" className="bg-background h-9" autoFocus />
          <Input data-testid="recover-pin-input" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="New owner PIN (4–6 digits)" className="bg-background h-9" />
          <Input data-testid="recover-pin-confirm-input" inputMode="numeric" maxLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} placeholder="Confirm new PIN" className="bg-background h-9" />
          <Button data-testid="recover-submit-btn" onClick={submit} disabled={busy} size="sm" className="w-full gap-2 bg-primary hover:bg-primary/90 rounded-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LifeBuoy className="h-4 w-4" />} Reset PIN & unlock
          </Button>
        </div>
      )}
    </div>
  );
};

export const ForgotPinForm = ({ onBack }) => {
  const { status } = useSecurity();
  const hint = status?.recovery_email_hint;
  const codeAvailable = status?.recovery_code_available;

  return (
    <motion.div key="forgot" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} data-testid="forgot-pin-form">
      <h1 className="mt-5 font-serif text-2xl font-medium">Reset the owner PIN</h1>
      <p className="mt-2 text-sm text-muted-foreground flex items-center justify-center gap-1.5">
        <LifeBuoy className="h-3.5 w-3.5" /> Choose how to prove it's you
      </p>
      <div className="mt-6 space-y-3">
        {hint && <EmailOption hint={hint} />}
        {codeAvailable && <CodeOption />}
        {!hint && !codeAvailable && (
          <p data-testid="recovery-unavailable" className="text-sm text-muted-foreground rounded-xl border border-border bg-card p-4 text-left">
            PIN recovery isn't switched on for this vault yet. The owner can add a recovery email in Security settings, or the deployer can set a{" "}
            <span className="font-mono text-foreground">VAULT_RESET_CODE</span> secret.
          </p>
        )}
      </div>
      <button data-testid="forgot-back-btn" onClick={onBack} className="mt-5 text-xs text-primary inline-flex items-center gap-0.5 hover:underline">
        <ChevronLeft className="h-3 w-3" /> Back to PIN
      </button>
    </motion.div>
  );
};
